// src/pages/auth/ResetPasswordPage.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import Button from '../../components/common/Button';
import InputField from '../../components/common/InputField';
import { verifyResetOTP, resetPassword } from '../../services/authService';
import { 
  FaLock, 
  FaEye, 
  FaEyeSlash,
  FaPlane, 
  FaCheckCircle,
  FaTimesCircle,
  FaArrowLeft,
  FaGlobe,
  FaUsers,
  FaHeart,
  FaKey
} from 'react-icons/fa';
import { MdOutlineMarkEmailRead } from 'react-icons/md';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('otp'); // 'otp' or 'password'
  const [userId, setUserId] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    minLength: false
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange'
  });

  const password = watch('password', '');
  const confirmPassword = watch('confirmPassword', '');

  // Check password strength in real-time
  React.useEffect(() => {
    if (password) {
      setPasswordStrength({
        score: calculatePasswordStrength(password),
        hasUpper: /[A-Z]/.test(password),
        hasLower: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        minLength: password.length >= 8
      });
    } else {
      setPasswordStrength({
        score: 0,
        hasUpper: false,
        hasLower: false,
        hasNumber: false,
        hasSpecial: false,
        minLength: false
      });
    }
  }, [password]);

  const calculatePasswordStrength = (pass) => {
    let score = 0;
    if (pass.length >= 8) score += 25;
    if (pass.length >= 12) score += 10;
    if (/[A-Z]/.test(pass)) score += 20;
    if (/[a-z]/.test(pass)) score += 20;
    if (/[0-9]/.test(pass)) score += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) score += 25;
    return Math.min(score, 100);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score < 40) return 'bg-red-500';
    if (passwordStrength.score < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score < 40) return 'Weak';
    if (passwordStrength.score < 70) return 'Medium';
    return 'Strong';
  };

  const onOTPSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Get userId from localStorage (saved during forgot password)
      const savedUserId = localStorage.getItem('resetUserId');
      
      if (!savedUserId) {
        toast.error('Session expired. Please start over.');
        navigate('/forgot-password');
        return;
      }

      const response = await verifyResetOTP(savedUserId, data.otp);

      if (response.success) {
        setUserId(savedUserId);
        setResetToken(response.data.resetToken);
        setStep('password');
        toast.success('OTP verified! Please set your new password.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid OTP. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await resetPassword(resetToken, data.password);

      if (response.success) {
        localStorage.removeItem('resetUserId');
        toast.success('Password reset successful! Welcome back!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Get email from localStorage (saved during forgot password)
  const userEmail = localStorage.getItem('resetEmail');

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 m-0 p-0 relative">
      {/* Fixed Background */}
      <div className="fixed inset-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute bottom-0 w-full h-48" preserveAspectRatio="none" viewBox="0 0 1440 120">
            <path fill="white" d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 transform rotate-12">
            <FaPlane className="text-white text-8xl" />
          </div>
          <div className="absolute bottom-10 right-10 transform -rotate-45">
            <FaPlane className="text-white text-8xl" />
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="relative z-10 min-h-screen w-full overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen py-8 md:py-12">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Welcome text */}
              <div className="text-center lg:text-left">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
                  🔐 {step === 'otp' ? 'Verify OTP' : 'Create New Password'}
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight text-white">
                  {step === 'otp' ? 'Enter Verification' : 'Reset Your'} 
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
                    {step === 'otp' ? 'OTP Code' : 'Password'}
                  </span>
                </h1>
                <p className="text-lg text-blue-100 max-w-xl mx-auto lg:mx-0 mb-6">
                  {step === 'otp' 
                    ? `We've sent a 6-digit OTP to ${userEmail || 'your email'}. Enter it below to verify your identity.`
                    : 'Choose a strong password that you haven\'t used before to keep your account secure.'}
                </p>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaGlobe className="text-yellow-300" />
                    <span className="text-white">Secure Encryption</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaUsers className="text-yellow-300" />
                    <span className="text-white">Instant Update</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaHeart className="text-yellow-300" />
                    <span className="text-white">24/7 Support</span>
                  </div>
                </div>
              </div>

              {/* Right side - Form Card */}
              <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-6 transform transition-all duration-500 hover:scale-105">
                  <div className="text-center mb-6">
                    <div className="inline-block p-3 bg-blue-100 rounded-full mb-3">
                      {step === 'otp' ? (
                        <FaKey className="text-blue-600 text-xl" />
                      ) : (
                        <FaLock className="text-blue-600 text-xl" />
                      )}
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {step === 'otp' ? 'Enter OTP Code' : 'Create New Password'}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      {step === 'otp' 
                        ? 'Check your email for the 6-digit code' 
                        : 'Your new password must be different from previous ones'}
                    </p>
                  </div>

                  {step === 'otp' ? (
                    <form onSubmit={handleSubmit(onOTPSubmit)} className="space-y-5">
                      {/* OTP Field */}
                      <div>
                        <InputField
                          label="6-Digit OTP"
                          type="text"
                          placeholder="Enter 6-digit OTP"
                          icon={<FaKey className="text-gray-400" />}
                          error={errors.otp}
                          {...register('otp', {
                            required: 'OTP is required',
                            pattern: {
                              value: /^[0-9]{6}$/,
                              message: 'Please enter a valid 6-digit OTP'
                            }
                          })}
                        />
                      </div>

                      {/* Resend OTP Link */}
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => {
                            toast.info('Requesting new OTP...');
                            navigate('/forgot-password');
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          Didn't receive OTP? Request again
                        </button>
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2 rounded-lg transition-all transform hover:scale-[1.02] disabled:opacity-50"
                        isLoading={isLoading}
                        disabled={isLoading || Object.keys(errors).length > 0}
                      >
                        Verify OTP
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleSubmit(onPasswordSubmit)} className="space-y-5">
                      {/* New Password Field */}
                      <div>
                        <div className="relative">
                          <InputField
                            label="New Password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter new password"
                            icon={<FaLock className="text-gray-400" />}
                            error={errors.password}
                            {...register('password', {
                              required: 'Password is required',
                              minLength: {
                                value: 8,
                                message: 'Password must be at least 8 characters'
                              },
                              validate: {
                                hasUpper: (value) => 
                                  /[A-Z]/.test(value) || 'Must contain uppercase letter',
                                hasLower: (value) => 
                                  /[a-z]/.test(value) || 'Must contain lowercase letter',
                                hasNumber: (value) => 
                                  /[0-9]/.test(value) || 'Must contain number',
                                hasSpecial: (value) => 
                                  /[!@#$%^&*(),.?":{}|<>]/.test(value) || 'Must contain special character'
                              }
                            })}
                            className="pr-12"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-12 text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                          </button>
                        </div>
                        
                        {/* Password Strength Meter */}
                        {password && (
                          <div className="mt-2 space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  style={{ width: `${passwordStrength.score}%` }}
                                  className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`}
                                />
                              </div>
                              <span className="ml-3 text-xs font-medium text-gray-600">
                                {getPasswordStrengthText()}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="flex items-center text-gray-600">
                                {passwordStrength.minLength ? 
                                  <FaCheckCircle className="text-green-500 mr-1" /> : 
                                  <FaTimesCircle className="text-gray-300 mr-1" />
                                } 8+ characters
                              </div>
                              <div className="flex items-center text-gray-600">
                                {passwordStrength.hasUpper ? 
                                  <FaCheckCircle className="text-green-500 mr-1" /> : 
                                  <FaTimesCircle className="text-gray-300 mr-1" />
                                } Uppercase
                              </div>
                              <div className="flex items-center text-gray-600">
                                {passwordStrength.hasLower ? 
                                  <FaCheckCircle className="text-green-500 mr-1" /> : 
                                  <FaTimesCircle className="text-gray-300 mr-1" />
                                } Lowercase
                              </div>
                              <div className="flex items-center text-gray-600">
                                {passwordStrength.hasNumber ? 
                                  <FaCheckCircle className="text-green-500 mr-1" /> : 
                                  <FaTimesCircle className="text-gray-300 mr-1" />
                                } Number
                              </div>
                              <div className="flex items-center text-gray-600 col-span-2">
                                {passwordStrength.hasSpecial ? 
                                  <FaCheckCircle className="text-green-500 mr-1" /> : 
                                  <FaTimesCircle className="text-gray-300 mr-1" />
                                } Special character
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Confirm Password Field */}
                      <div>
                        <div className="relative">
                          <InputField
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            icon={<FaLock className="text-gray-400" />}
                            error={errors.confirmPassword}
                            {...register('confirmPassword', {
                              required: 'Please confirm your password',
                              validate: value => value === password || 'Passwords do not match'
                            })}
                            className="pr-12"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-12 text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                          </button>
                        </div>
                        {confirmPassword && password === confirmPassword && (
                          <p className="mt-1 text-xs text-green-600 flex items-center">
                            <FaCheckCircle className="mr-1" /> Passwords match
                          </p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2 rounded-lg transition-all transform hover:scale-[1.02] disabled:opacity-50"
                        isLoading={isLoading}
                        disabled={isLoading || Object.keys(errors).length > 0}
                      >
                        Reset Password
                      </Button>
                    </form>
                  )}

                  {/* Back to Login */}
                  <div className="mt-4 text-center">
                    <Link 
                      to="/login" 
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <FaArrowLeft className="mr-2 text-xs" /> Back to Login
                    </Link>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FaCheckCircle className="text-green-500" /> Encrypted
                      </span>
                      <span className="flex items-center gap-1">
                        <FaUsers className="text-blue-500" /> Secure Update
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default ResetPasswordPage;